import { FunctionComponent, ReactNode, useCallback, useState } from 'react';

import {
  DialogContent,
  DialogContentProps,
  DialogOverlay,
} from '@reach/dialog';
import { m as motion } from 'framer-motion';
import { useTimeoutFn } from 'react-use';
import styled from 'styled-components';

import { WarningIcon } from 'chime-ui/components/Icons';
import { StandardText } from 'chime-ui/components/Typography';
import { colors, values } from 'chime-ui/system';
import media from 'chime-ui/system/media';

type ErrorDialogProps = {
  onShow?: () => void;
  onDismiss?: () => void;
};

type ErrorDialogReturn = {
  Modal: FunctionComponent<ModalProps>;
  isShowing: boolean;
  handleShow: () => void;
  handleDismiss: () => void;
};

type ModalProps = {
  children: ReactNode;
};

const useErrorAlert = ({
  onShow,
  onDismiss,
}: ErrorDialogProps = {}): ErrorDialogReturn => {
  const [isShowing, setIsShowing] = useState(false);

  const handleShow = useCallback(() => {
    setIsShowing(true);
    if (typeof onShow === 'function') {
      onShow();
    }
  }, [onShow]);

  const handleDismiss = useCallback(() => {
    setIsShowing(false);
    if (typeof onDismiss === 'function') {
      onDismiss();
    }
  }, [onDismiss]);

  const MotionDialogContent = motion<DialogContentProps>(StyledDialogContent, {
    forwardMotionProps: true,
  });

  function Modal({ children }) {
    useTimeoutFn(() => {
      handleDismiss();
    }, values.MESSAGE_TIMEOUT);

    return (
      <StyledDialogOverlay
        dangerouslyBypassFocusLock
        dangerouslyBypassScrollLock
      >
        <MotionDialogContent
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: 'easeIn', delay: values.TIMEOUT_DELAY }}
          aria-label="Error Dialog"
        >
          <Message>
            <StandardText>
              <WarningIcon />
              {children}
            </StandardText>
          </Message>
        </MotionDialogContent>
      </StyledDialogOverlay>
    );
  }

  return {
    Modal,
    isShowing,
    handleShow,
    handleDismiss,
  };
};

export default useErrorAlert;
const StyledDialogOverlay = styled(DialogOverlay)`
  background: none;
  bottom: 75%;
`;

const StyledDialogContent = styled(DialogContent)`
  font-family: ${props => props.theme.fonts.primaryFont};
  background: ${props => props.theme.colors.white};
  position: fixed;
  padding: 0.5rem 1rem;
  background: ${colors.tint.alertRed.a10};
  display: block;
  border-radius: 0.5rem;
  width: auto;
  margin: 0;

  ${media.mobileOnly`
    top: 1rem;
    left: 1.5rem;
    right: 1.5rem;
  `}

  ${media.tablet`
    top: 5.75rem;
    width: 32.5rem;
    left: 50%;
    transform: translateX(-50%);
  `}
`;

const Message = styled.div`
  position: relative;
  margin: 0;
  span {
    white-space: pre-wrap;
  }
`;
